import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useQuery} from '@github-ui/react-query'
import {Link, useParams} from '@github-ui/react-router'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useSearchParams} from '@github-ui/use-navigate'
import {AlertIcon, BookIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {useSavedViewAnalytics} from '../../hooks/use-saved-view-analytics'
import {savedViewsQueryOptions} from '../../queries/saved-views'
import styles from './RepositoryViews.module.css'
import {DEFAULT_VIEWS_SORT, isViewsSortValue, sortViews, type ViewsSortValue} from './views-sort-options'
import {ViewsList} from './ViewsList'

export function ViewsIndexContent({newViewUrl}: {newViewUrl: string}) {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const [searchParams, setSearchParams] = useSearchParams()
  const sortParam = searchParams.get('sort')
  const sort = isViewsSortValue(sortParam) ? sortParam : DEFAULT_VIEWS_SORT
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  const handleSortChange = useCallback(
    (next: ViewsSortValue) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (next === DEFAULT_VIEWS_SORT) {
            params.delete('sort')
          } else {
            params.set('sort', next)
          }
          return params
        },
        {replace: true, preventAutofocus: true},
      )
    },
    [setSearchParams],
  )

  const {trackIndexVisited} = useSavedViewAnalytics()
  const hasTrackedVisitRef = useRef(false)
  useEffect(() => {
    if (hasTrackedVisitRef.current) return
    hasTrackedVisitRef.current = true
    trackIndexVisited()
  }, [trackIndexVisited])

  const {data, isError, isFetching, refetch} = useQuery(savedViewsQueryOptions({owner, repo}))
  const sortedViews = useMemo(() => sortViews(data?.savedViews ?? [], sort), [data?.savedViews, sort])
  const viewerCanManageViews = data?.viewerCanManageViews ?? false
  const isLoading = isFetching && !data && !isError
  const showError = isError && !data
  const hasViews = !!data && sortedViews.length > 0

  const errorBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.indexErrorTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.indexLoadErrorDescription}</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => refetch()}>{BUTTON_LABELS.tryAgain}</Blankslate.PrimaryAction>
    </Blankslate>
  )

  const renderErrorFallback = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.indexErrorTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.indexDisplayErrorDescription}</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => window.location.reload()}>
        {BUTTON_LABELS.reloadPage}
      </Blankslate.PrimaryAction>
    </Blankslate>
  )

  const emptyBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <BookIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.emptyHeading}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.emptyDescription}</Blankslate.Description>
      {viewerCanManageViews && (
        <Button as={Link} to={newViewUrl} variant="primary" className={styles.blankslateAction}>
          {BUTTON_LABELS.newView}
        </Button>
      )}
    </Blankslate>
  )

  const listProps = {views: sortedViews, owner, repo, sort, onSortChange: handleSortChange, viewerCanManageViews}

  return (
    <div className={clsx(styles.viewsPage, sidebarLayoutEnabled && 'mt-0')}>
      {!sidebarLayoutEnabled && (
        <div className={styles.headerRow}>
          <Heading as="h1" className={styles.heading}>
            {LABELS.viewsTitle}
          </Heading>
          {hasViews && viewerCanManageViews && (
            <Button as={Link} to={newViewUrl} variant="primary">
              {BUTTON_LABELS.newView}
            </Button>
          )}
        </div>
      )}
      <div className={styles.viewsListWrapper} data-hpc containertiming="hpc" aria-busy={isLoading}>
        <ErrorBoundary boundaryName="RepositoryViewsIndexPage" fallback={renderErrorFallback}>
          {isLoading ? (
            <ViewsList {...listProps} isLoading />
          ) : showError ? (
            errorBlankslate
          ) : (
            <ViewsList {...listProps} emptyState={emptyBlankslate} />
          )}
        </ErrorBoundary>
      </div>
    </div>
  )
}
